import React, { useState, useEffect } from "react";
import { UserWithRole } from "@/types/iam";

interface RoleEditDialogProps {
  isOpen: boolean;
  user: UserWithRole;
  onClose: () => void;
  onSave: (isAdmin: boolean, isStaff: boolean, isDeveloper: boolean, isObserver: boolean) => Promise<void>;
  loading?: boolean;
}

export default function RoleEditDialog({
  isOpen,
  user,
  onClose,
  onSave,
  loading = false,
}: RoleEditDialogProps) {
  const [isAdmin, setIsAdmin] = useState(user.isAdmin);
  const [isStaff, setIsStaff] = useState(user.isStaff);
  const [isDeveloper, setIsDeveloper] = useState(user.isDeveloper);
  const [isObserver, setIsObserver] = useState(user.isObserver);

  // 當用戶變更時，更新本地狀態
  useEffect(() => {
    setIsAdmin(user.isAdmin);
    setIsStaff(user.isStaff);
    setIsDeveloper(user.isDeveloper);
    setIsObserver(user.isObserver);
  }, [user]);

  const handleSave = async () => {
    await onSave(isAdmin, isStaff, isDeveloper, isObserver);
  };

  const handleClose = () => {
    // 重置為原始值
    setIsAdmin(user.isAdmin);
    setIsStaff(user.isStaff);
    setIsDeveloper(user.isDeveloper);
    setIsObserver(user.isObserver);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
      <div className="bg-white dark:bg-surface-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4">
          調整權限 - {user.displayName || user.email}
        </h3>
        
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer flex-1">
              <input
                type="checkbox"
                checked={isAdmin}
                onChange={(e) => {
                  const newAdminStatus = e.target.checked;
                  setIsAdmin(newAdminStatus);
                  // 如果設置為 Admin，自動啟用其他所有角色
                  if (newAdminStatus) {
                    setIsStaff(true);
                    setIsDeveloper(true);
                    setIsObserver(true);
                  }
                }}
                disabled={loading}
                className="w-5 h-5 text-primary border-surface-300 rounded focus:ring-primary"
              />
              <div>
                <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                  管理員 (Admin)
                </span>
                <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">
                  擁有系統所有權限，包括管理其他用戶的權限
                </p>
              </div>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer flex-1">
              <input
                type="checkbox"
                checked={isStaff}
                onChange={(e) => {
                  const newStaffStatus = e.target.checked;
                  setIsStaff(newStaffStatus);
                  // 如果取消 Staff，且不是 Admin，則取消 Developer
                  if (!newStaffStatus && !isAdmin) {
                    setIsDeveloper(false);
                  }
                }}
                disabled={loading || isAdmin}
                className="w-5 h-5 text-primary border-surface-300 rounded focus:ring-primary disabled:opacity-50"
              />
              <div>
                <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                  工作人員 (Staff)
                </span>
                <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">
                  可以管理內容和進行日常營運操作
                </p>
              </div>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer flex-1">
              <input
                type="checkbox"
                checked={isDeveloper}
                onChange={(e) => {
                  const newDeveloperStatus = e.target.checked;
                  setIsDeveloper(newDeveloperStatus);
                  // Developer 包含 Observer 權限，所以自動啟用 Observer
                  if (newDeveloperStatus) {
                    setIsObserver(true);
                  } else {
                    // 如果取消 Developer，且用戶沒有其他 staff 權限，則取消 Observer
                    // 但如果用戶是 admin 或 staff，則保留 observer（因為他們可能有其他權限）
                    if (!isAdmin && !isStaff) {
                      setIsObserver(false);
                    }
                  }
                  // 如果設置為 Developer，且不是 Admin，則啟用 Staff（因為 Developer 有 Staff 權限）
                  if (newDeveloperStatus && !isAdmin) {
                    setIsStaff(true);
                  }
                }}
                disabled={loading || isAdmin}
                className="w-5 h-5 text-primary border-surface-300 rounded focus:ring-primary disabled:opacity-50"
              />
              <div>
                <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                  開發者 (Developer)
                </span>
                <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">
                  與工作人員相同權限，可以管理內容和進行日常營運操作
                </p>
              </div>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer flex-1">
              <input
                type="checkbox"
                checked={isObserver}
                onChange={(e) => setIsObserver(e.target.checked)}
                disabled={loading || isAdmin || isDeveloper}
                className="w-5 h-5 text-primary border-surface-300 rounded focus:ring-primary disabled:opacity-50"
              />
              <div>
                <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                  觀察者 (Observer)
                </span>
                <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">
                  只能查看所有資料，不能編輯、刪除或管理
                </p>
              </div>
            </label>
          </div>

          {isAdmin && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                注意：管理員權限已包含所有其他角色的功能
              </p>
            </div>
          )}
          {isDeveloper && !isAdmin && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
              <p className="text-xs text-purple-700 dark:text-purple-300">
                注意：開發者權限已包含觀察者權限
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 rounded-lg hover:bg-surface-300 dark:hover:bg-surface-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            儲存
          </button>
        </div>
      </div>
    </div>
  );
}

